import { css, html, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';
import '../luna-icon/luna-icon';
import '../luna-button/luna-button';

// Confirm Dialog — figma "Confirm Dialog" (component 245:85), for actions that
// discard progress (Restart, New game). Always two clear choices: Cancel
// (default focus) and a labelled Confirm — never colour alone. Per the
// component description: role=alertdialog, focus starts on Cancel, ESC = Cancel.
@customElement('luna-confirm-dialog')
export class LunaConfirmDialog extends LitPage {
  @property({ type: Boolean }) open = false;
  @property({ attribute: 'title' }) title = '';
  @property({ attribute: 'body' }) body = '';
  @property({ attribute: 'confirm-label' }) confirmLabel = '';

  private _focusReturn: Element | null = null;

  updated(changed: PropertyValues): void {
    if (changed.has('open')) {
      if (this.open) {
        void this.activate();
      } else {
        this.deactivate();
      }
    }
  }

  private async activate(): Promise<void> {
    this._focusReturn = document.activeElement;
    document.body.style.overflow = 'hidden';
    // Focus starts on Cancel. The luna-button children render in their own
    // update cycle, so wait for one before reaching for its inner <button>.
    const cancel = this.querySelector('.luna-confirm-dialog__cancel');
    await (cancel as LitPage | null)?.updateComplete;
    this.querySelector<HTMLButtonElement>('.luna-confirm-dialog__cancel button')?.focus();
  }

  private deactivate(): void {
    document.body.style.overflow = '';
    const target = this._focusReturn;
    this._focusReturn = null;
    if (target instanceof HTMLElement) {
      target.focus();
    }
  }

  private cancel(): void {
    if (!this.open) {
      return;
    }
    this.open = false;
    this.dispatchEvent(new CustomEvent('luna-cancel', { detail: {}, bubbles: true, composed: true }));
  }

  private confirm(): void {
    if (!this.open) {
      return;
    }
    this.open = false;
    this.dispatchEvent(new CustomEvent('luna-confirm', { detail: {}, bubbles: true, composed: true }));
  }

  private onScrimClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.cancel();
    }
  }

  private onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancel();
      return;
    }
    if (event.key !== 'Tab') {
      return;
    }

    const focusables = this.focusables();
    if (focusables.length === 0) {
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private focusables(): HTMLElement[] {
    return Array.from(
      this.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled'));
  }

  content() {
    if (!this.open) {
      return html``;
    }

    return html`
      <div
        class="luna-confirm-dialog__scrim"
        @click=${this.onScrimClick}
        @keydown=${this.onKeydown}
      >
        <div
          class="luna-confirm-dialog"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="luna-confirm-dialog-title"
          aria-describedby="luna-confirm-dialog-body"
        >
          <span class="luna-confirm-dialog__chip">
            <luna-icon name="help" size="24" aria-hidden="true"></luna-icon>
          </span>
          <h2
            class="luna-confirm-dialog__title mbw-type-title-card"
            id="luna-confirm-dialog-title"
          >
            ${this.title}
          </h2>
          <p class="luna-confirm-dialog__body mbw-type-body-default" id="luna-confirm-dialog-body">
            ${this.body}
          </p>
          <div class="luna-confirm-dialog__actions">
            <luna-button
              class="luna-confirm-dialog__cancel"
              variant="neutral"
              full
              @click=${this.cancel}
              >Cancel</luna-button
            >
            <luna-button variant="success" full @click=${this.confirm}
              ><span>${this.confirmLabel}</span></luna-button
            >
          </div>
        </div>
      </div>
    `;
  }

  styles() {
    return css`
      .luna-confirm-dialog__scrim {
        position: fixed;
        inset: 0;
        /* Above the sticky top bar (z-index 10), same as the how-to overlay. */
        z-index: 20;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--luna-surface-scrim);
      }

      .luna-confirm-dialog {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 16px; /* pinned: figma Confirm Dialog */
        box-sizing: border-box;
        width: 420px; /* pinned: figma Confirm Dialog */
        max-width: calc(100% - 32px);
        padding: 28px 28px 24px; /* pinned: figma Confirm Dialog */
        border-radius: var(--luna-radius-card-sm);
        background: #ffffff; /* pinned: figma Confirm Dialog */
        box-shadow:
          inset 0 0 0 1px rgba(255, 255, 255, 0.6),
          0 0 0 1px var(--luna-edge-blue),
          0 24px 60px rgba(0, 0, 0, 0.4); /* pinned: figma luna/modal-shadow */
      }

      .luna-confirm-dialog__chip {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 52px; /* pinned: figma Confirm Dialog */
        height: 52px; /* pinned: figma Confirm Dialog */
        border-radius: var(--luna-radius-pill);
        background: #fdf1e0; /* pinned: figma Confirm Dialog (caution chip) */
        color: #f1c21b; /* pinned: figma Confirm Dialog (help icon) */
      }

      .luna-confirm-dialog__title {
        margin: 0;
        color: var(--mbw-text-ink);
      }

      .luna-confirm-dialog__body {
        margin: 0;
        color: var(--mbw-text-body);
      }

      .luna-confirm-dialog__actions {
        display: flex;
        gap: 12px; /* pinned: figma Confirm Dialog */
        width: 100%;
      }

      .luna-confirm-dialog__actions luna-button {
        flex: 1;
      }
    `;
  }
}
